// Merge pack template settings.json vào project settings.json.
//
// Background:
//   - Pack ship templates/settings.json.tpl với `hooks`, `statusLine`, `permissions`
//     declare workflow team-shared (dev-rules-reminder, scout-block, ...).
//   - Avatar `init` ghi base settings.json với env Avatar vars + permissions baseline.
//   - Sau khi `sync` symlink farm xong, hooks pack KHÔNG active vì project
//     settings.json chưa khai báo hook entries → Claude Code không trigger.
//
// Strategy (Q1 confirmed: "Pack base + user override"):
//   - Pack template = baseline cho hooks + statusLine + permissions.
//   - User customization (env, model, existing permissions extras, existing hook entries
//     dùng matcher khác) → preserve.
//   - Deep merge field-level:
//       env             → user wins (giữ API key)
//       statusLine      → pack wins nếu user chưa set
//       permissions     → union (pack + user, dedupe)
//       hooks           → merge per-event, dedupe theo (matcher + command)
//       model           → user wins nếu set; pack default fallback
//       includeCoAuthoredBy → pack value áp dụng nếu user chưa set
//
// Safety:
//   - Backup .claude/settings.json → .claude/settings.json.backup-<timestamp> trước merge
//   - Atomic write file mới
//   - Return result để caller log từng action

import { promises as fs } from "node:fs";
import path, { join } from "node:path";
import { pathExists, readJson, readText, writeJsonAtomic } from "./filesystem-helpers.js";
import { log } from "./terminal-logger.js";

export interface PackSettingsTemplate {
  $schema?: string;
  includeCoAuthoredBy?: boolean;
  // Avatar tắt Dynamic Workflows của Claude Code trong workspace (token cost +
  // kiểm soát). Ghi lúc init, GIỮ NGUYÊN qua merge (spread userSettings) — pack
  // không đụng field này nên không bị ghi đè.
  disableWorkflows?: boolean;
  model?: string;
  env?: Record<string, string>;
  permissions?: { allow?: string[]; deny?: string[] };
  hooks?: Record<string, unknown[]>;
  statusLine?: { type: string; command: string; padding?: number };
}

export interface MergeSettingsResult {
  action: "merged" | "no-change" | "no-pack-template";
  backupPath?: string;
  changes: string[]; // human-readable list of fields touched
}

// Token "trông giống path" cần kiểm shape: absolute unix (`/...`), home (`~/...`),
// relative-explicit (`./...`, `../...`), Windows drive letter (`C:\...`), hoặc
// chứa backslash (Windows path separator). Token KHÔNG khớp pattern này (vd
// tên interpreter "node", "npx", flag "--foo", hay path tương đối kiểu
// ".claude/hooks/x.py" không có "./" prefix) được xử lý riêng bên dưới.
const PATH_LIKE_TOKEN = /^(\/|~\/|\.\.?\/|[A-Za-z]:[\\/]|.*\\)/;

// Kiểm 1 token path-like có SHAPE an toàn không (không absolute, không traversal
// ra ngoài workspace). Dùng chung cho mọi token nghi ngờ trong command.
function checkPathToken(
  workspacePath: string,
  filePath: string,
): { safe: boolean; reason?: string } {
  if (filePath.includes("${CLAUDE_PROJECT_DIR}")) {
    // Biến expand runtime, gốc luôn là workspace — an toàn về shape, trừ khi
    // cố lách bằng "..".
    if (filePath.includes("..")) {
      return { safe: false, reason: `path traversal trong "${filePath}"` };
    }
    return { safe: true };
  }
  if (
    filePath.startsWith("/") ||
    filePath.startsWith("~/") ||
    /^[A-Za-z]:[\\/]/.test(filePath) // Windows drive letter absolute path
  ) {
    return {
      safe: false,
      reason: `absolute path "${filePath}" (chỉ accept relative tới workspace)`,
    };
  }
  const wsResolved = path.resolve(workspacePath);
  const targetResolved = path.resolve(join(workspacePath, filePath));
  if (!targetResolved.startsWith(`${wsResolved}/`) && targetResolved !== wsResolved) {
    return { safe: false, reason: `path "${filePath}" resolve ra ngoài workspace (traversal)` };
  }
  return { safe: true };
}

// SECURITY: command từ pack template/manifest có path file trỏ RA NGOÀI workspace
// không? Reject absolute path + path traversal (`../`). Đây là chốt chặn supply
// chain: pack compromise có thể inject hook trỏ `/etc/...` hoặc `../../evil`.
// KHÔNG check file tồn tại (đó là việc của isStatusLineCommandResolvable) — chỉ
// về SHAPE của path. Command shell thuần (echo/date) hoặc dùng ${CLAUDE_PROJECT_DIR}
// → coi là an toàn về path (biến expand runtime trong workspace).
//
// H1 fix (VULN-1 fail-open regression): version cũ chỉ check path SAU MỘT
// interpreter cố định trong danh sách (node|python|python3|bash|sh) — bất kỳ
// command dùng interpreter/path KHÁC (npx, node.exe, deno, hoặc path binary
// tuyệt đối không qua interpreter nào) đều lọt qua fail-open ({safe:true}).
// Giờ quét TẤT CẢ token trong command: token đầu tiên (có thể là binary tuyệt
// đối, vd "/usr/bin/evil") VÀ mọi token theo sau (có thể là script path sau
// 1 interpreter bất kỳ, vd "npx tsx /etc/evil.ts") đều bị kiểm shape nếu
// "trông giống path" (xem PATH_LIKE_TOKEN). Token không giống path (tên
// interpreter trần, flag, path tương đối không có "./" prefix) được bỏ qua —
// không đổi hành vi cho hook hợp lệ dạng "node .claude/hooks/x.py" hay
// "python3 ${CLAUDE_PROJECT_DIR}/.claude/pack/x.py".
// Trả { safe, reason } để caller log lý do cụ thể.
export function isCommandPathWithinWorkspace(
  workspacePath: string,
  command: string,
): { safe: boolean; reason?: string } {
  const tokens = command.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return { safe: true };

  // Legacy behavior (giữ nguyên): "<interpreter> <path>" với interpreter tên
  // trần quen thuộc + path KHÔNG có "./"/"../" prefix (vd ".claude/hooks/x.py")
  // vẫn được resolve tương đối tới workspace như trước — không phá test cũ.
  const legacyMatch = command.trim().match(/^(node|python|python3|bash|sh)\s+([^\s]+)/);
  if (legacyMatch?.[2] && !PATH_LIKE_TOKEN.test(legacyMatch[2])) {
    const check = checkPathToken(workspacePath, legacyMatch[2]);
    if (!check.safe) return check;
  }

  // Quét MỌI token (kể cả token đầu — command không qua interpreter nào, vd
  // "/usr/bin/evil") xem có token nào "giống path" mà shape KHÔNG an toàn.
  for (const token of tokens) {
    if (!PATH_LIKE_TOKEN.test(token)) continue;
    const check = checkPathToken(workspacePath, token);
    if (!check.safe) return check;
  }

  return { safe: true };
}

// v1.7.0: Verify statusLine command file ref có thật trong workspace.
// Pack template thường ref `node .claude/statusline.cjs` hoặc shell command absolute.
// Parse simple: nếu là `node <path>` hoặc `python3 <path>` → check path tồn tại.
// Nếu là shell command khác (vd `echo`, `date`) → assume OK (không Avatar's concern).
async function isStatusLineCommandResolvable(
  workspacePath: string,
  command: string,
): Promise<boolean> {
  // SHAPE check trước (absolute / traversal) — dùng chung với hook validation.
  const pathCheck = isCommandPathWithinWorkspace(workspacePath, command);
  if (!pathCheck.safe) {
    log.warn(`Pack statusLine reject: ${pathCheck.reason}.`);
    return false;
  }
  const match = command.trim().match(/^(node|python|python3|bash|sh)\s+([^\s]+)/);
  if (!match?.[2] || match[2].includes("${CLAUDE_PROJECT_DIR}")) {
    return true; // shell thuần hoặc runtime-var path — không check tồn tại tĩnh.
  }
  return await pathExists(join(workspacePath, match[2]));
}

// Generate backup filename với timestamp YYMMDD-HHMMSS để debug + restore.
// Exported: tái dùng cho apply-tool-manifest (enable/disable cũng backup trước).
// Có GIÂY (SS): 2 lần backup trong cùng phút KHÔNG overwrite snapshot trước —
// tránh mất bản backup gốc khi thao tác liên tiếp (CI/scripted).
export function backupFilename(originalPath: string): string {
  const d = new Date();
  const stamp = `${
    d.getFullYear().toString().slice(-2) +
    String(d.getMonth() + 1).padStart(2, "0") +
    String(d.getDate()).padStart(2, "0")
  }-${String(d.getHours()).padStart(2, "0")}${String(d.getMinutes()).padStart(2, "0")}${String(
    d.getSeconds(),
  ).padStart(2, "0")}`;
  return `${originalPath}.backup-${stamp}`;
}

// Dedupe + union 2 arrays giữ thứ tự (first occurrence wins).
// Exported: tái dùng cho apply-tool-manifest (union deny + dedupe hooks).
export function unionDedupe<T>(a: T[], b: T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const item of [...a, ...b]) {
    const key = typeof item === "string" ? item : JSON.stringify(item);
    if (!seen.has(key)) {
      seen.add(key);
      out.push(item);
    }
  }
  return out;
}

// Normalize command để so sánh "logically same hook" giữa old relative-path
// và new ${CLAUDE_PROJECT_DIR}-path. Strip prefix để hai version cùng hook
// có chung normalized form.
//
// Examples:
//   "node .claude/hooks/foo.cjs"                        → "node .claude/hooks/foo.cjs"
//   "node ${CLAUDE_PROJECT_DIR}/.claude/hooks/foo.cjs"  → "node .claude/hooks/foo.cjs"
//   → cùng normalized form → coi là duplicate.
function normalizeCommandForMigration(command: string): string {
  return command.replace(/\$\{CLAUDE_PROJECT_DIR\}\//g, "").trim();
}

// Extract command string từ hook entry (best-effort — return all commands joined
// nếu entry có nhiều hooks; mỗi entry thường chỉ có 1 hook).
function extractCommandsFromEntry(entry: unknown): string[] {
  if (typeof entry !== "object" || entry === null) return [];
  const obj = entry as Record<string, unknown>;
  const hooks = Array.isArray(obj.hooks) ? obj.hooks : [];
  const cmds: string[] = [];
  for (const h of hooks) {
    if (typeof h === "object" && h !== null) {
      const cmd = (h as Record<string, unknown>).command;
      if (typeof cmd === "string") cmds.push(cmd);
    }
  }
  return cmds;
}

// v1.18.0 MIGRATION: Loại bỏ stale relative-path entries khi pack template/tool
// manifest đã chuyển sang ${CLAUDE_PROJECT_DIR}/. Background: v0.7.0 pack ship
// relative path "node .claude/hooks/foo.cjs"; Bash tool cd vào subdir làm cwd
// kẹt → hook fail ENOENT. v0.7.1 pack đổi sang "node ${CLAUDE_PROJECT_DIR}/...".
// Nhưng user settings.json đã có entry cũ — merge mới chỉ APPEND new, không xóa
// old → cả 2 entries cùng tồn tại, old fail noisy mỗi prompt.
//
// Migration logic:
// - Trong mỗi event, group entries theo normalized command.
// - Nếu group có cả old (no ${CLAUDE_PROJECT_DIR}) + new (with) → drop old, keep new.
// - Group chỉ có 1 version → giữ nguyên (không opinion về user's custom hooks).
//
// Returns: { migrated entries, count of dropped stale entries per event }
function migrateStaleRelativePathHooks(entries: unknown[]): {
  entries: unknown[];
  droppedCount: number;
} {
  // Group by normalized command form
  type IndexedEntry = { index: number; entry: unknown; commands: string[]; hasVar: boolean };
  const indexed: IndexedEntry[] = entries.map((entry, index) => {
    const commands = extractCommandsFromEntry(entry);
    const hasVar = commands.some((c) => c.includes("${CLAUDE_PROJECT_DIR}"));
    return { index, entry, commands, hasVar };
  });

  // Build normalized-command → list of indexed entries
  const byNormalized = new Map<string, IndexedEntry[]>();
  for (const ie of indexed) {
    const key = ie.commands.map(normalizeCommandForMigration).sort().join("|");
    if (!key) continue; // skip entries with no commands
    const list = byNormalized.get(key) ?? [];
    list.push(ie);
    byNormalized.set(key, list);
  }

  // Find indices to drop: groups with both old + new → drop old.
  const toDrop = new Set<number>();
  for (const [, items] of byNormalized) {
    if (items.length < 2) continue;
    const hasAnyNew = items.some((it) => it.hasVar);
    if (!hasAnyNew) continue;
    for (const it of items) {
      if (!it.hasVar) toDrop.add(it.index);
    }
  }

  if (toDrop.size === 0) {
    return { entries, droppedCount: 0 };
  }

  const filtered = entries.filter((_, i) => !toDrop.has(i));
  return { entries: filtered, droppedCount: toDrop.size };
}

// Merge hooks per-event: pack entries + user entries, dedupe theo (matcher + commands).
// Schema Claude Code: { EventName: [ { matcher?: string, hooks: [{type, command}] } ] }
//
// v1.18.0: Sau khi union dedupe, chạy migrateStaleRelativePathHooks để xóa entries
// relative-path cũ nếu đã có version ${CLAUDE_PROJECT_DIR} mới — fix cho upgrade
// từ pack pre-v0.7.1 lên v0.7.1+ (settings.json không tự cleanup khi tool.json
// đổi command string).
function mergeHooksPerEvent(
  packHooks: Record<string, unknown[]>,
  userHooks: Record<string, unknown[]>,
): { merged: Record<string, unknown[]>; touchedEvents: string[]; migratedCount: number } {
  const touched: string[] = [];
  const merged: Record<string, unknown[]> = { ...userHooks };
  let totalMigrated = 0;

  // First migrate existing user entries (handle case where user has stale entries
  // not from pack — eg. legacy install before sync).
  for (const event of Object.keys(merged)) {
    const userEntries = (merged[event] as unknown[]) || [];
    const { entries: migrated, droppedCount } = migrateStaleRelativePathHooks(userEntries);
    if (droppedCount > 0) {
      merged[event] = migrated;
      totalMigrated += droppedCount;
      if (!touched.includes(event)) touched.push(event);
    }
  }

  // Then union with pack entries + dedupe (existing behavior).
  for (const [event, packEntries] of Object.entries(packHooks)) {
    const userEntries = (merged[event] as unknown[]) || [];
    const union = unionDedupe(userEntries, packEntries);
    // Run migration again after union (in case pack new + user old now coexist).
    const { entries: postMigrate, droppedCount: postDropped } =
      migrateStaleRelativePathHooks(union);
    if (postDropped > 0) {
      totalMigrated += postDropped;
    }
    if (postMigrate.length !== userEntries.length) {
      if (!touched.includes(event)) touched.push(event);
    }
    merged[event] = postMigrate;
  }
  return { merged, touchedEvents: touched, migratedCount: totalMigrated };
}

// Main entry. Đọc pack template + user settings, merge, backup + write atomic.
// Returns result để caller report.
export async function mergePackSettingsIntoProjectSettings(
  workspacePath: string,
): Promise<MergeSettingsResult> {
  const packTemplatePath = join(workspacePath, ".claude", "pack", "templates", "settings.json.tpl");
  const projectSettingsPath = join(workspacePath, ".claude", "settings.json");

  // Pack chưa sync hoặc không có template → no-op.
  if (!(await pathExists(packTemplatePath))) {
    return { action: "no-pack-template", changes: [] };
  }

  // Pack template có thể chứa placeholders {{...}}; chúng ta merge nguyên trạng
  // (placeholder sẽ giữ literal trong project settings nếu user chưa thay).
  // Tuy nhiên hooks + statusLine + permissions không dùng placeholder → safe.
  let packTemplate: PackSettingsTemplate;
  try {
    const raw = await readText(packTemplatePath);
    packTemplate = JSON.parse(raw) as PackSettingsTemplate;
  } catch (err) {
    throw new Error(
      `Pack settings template không parse được JSON: ${(err as Error).message}. Path: ${packTemplatePath}`,
    );
  }

  // Project settings — nếu chưa có (vd init bị skip) thì coi như empty base.
  let userSettings: PackSettingsTemplate = {};
  let projectHasSettings = false;
  if (await pathExists(projectSettingsPath)) {
    projectHasSettings = true;
    try {
      userSettings = await readJson<PackSettingsTemplate>(projectSettingsPath);
    } catch (err) {
      throw new Error(
        `Project settings.json không parse được: ${(err as Error).message}. Manual fix trước khi sync.`,
      );
    }
  }

  const changes: string[] = [];
  const merged: PackSettingsTemplate = { ...userSettings };

  // 0. Avatar policy: tắt Dynamic Workflows của Claude Code trong workspace Avatar.
  // Áp cho CẢ workspace cũ (init trước khi có policy này) — nếu user CHƯA tự set
  // field này, Avatar set true. User chủ động đặt false → tôn trọng, không đè.
  if (typeof userSettings.disableWorkflows !== "boolean") {
    merged.disableWorkflows = true;
    changes.push("disableWorkflows=true (Avatar tắt Dynamic Workflows)");
  }

  // 1. statusLine — pack wins nếu user chưa set, NHƯNG verify file ref tồn tại.
  // v1.7.0 fix: trước đây merge blindly → settings ref `node .claude/statusline.cjs`
  // nhưng pack KHÔNG ship file đó → Claude Code fail im lặng render statusbar.
  // Giờ check: command pack ref tồn tại trong workspace? Nếu không → skip + warn.
  if (packTemplate.statusLine && !userSettings.statusLine) {
    const statusLineOk = await isStatusLineCommandResolvable(
      workspacePath,
      packTemplate.statusLine.command,
    );
    if (statusLineOk) {
      merged.statusLine = packTemplate.statusLine;
      changes.push("statusLine added");
    } else {
      changes.push(
        `statusLine SKIPPED (file ref '${packTemplate.statusLine.command}' không tồn tại)`,
      );
    }
  }

  // 2. includeCoAuthoredBy — pack wins nếu user chưa set.
  if (
    typeof packTemplate.includeCoAuthoredBy === "boolean" &&
    typeof userSettings.includeCoAuthoredBy !== "boolean"
  ) {
    merged.includeCoAuthoredBy = packTemplate.includeCoAuthoredBy;
    changes.push("includeCoAuthoredBy added");
  }

  // 3. model — user wins; chỉ add nếu pack có và user chưa set.
  if (packTemplate.model && !userSettings.model) {
    merged.model = packTemplate.model;
    changes.push("model added");
  }

  // 4. env — user wins entirely (giữ API key). Chỉ add var pack có mà user chưa có.
  // v1.20.0: Auto-rewrite legacy domain llm.nal.vn → ai.nal.vn nếu user có
  // settings.json từ pre-v1.20.0 (NAL gateway đổi domain). Silent migration,
  // không cần user confirm — cùng infra rename.
  if (packTemplate.env || userSettings.env) {
    const mergedEnv: Record<string, string> = { ...(userSettings.env || {}) };
    // 2 cờ RIÊNG để message không lẫn nhau: domain-rewrite và pack-var-added là
    // 2 thay đổi độc lập, mỗi cái log message riêng (trước đây dùng chung 1 cờ +
    // substring match "env vars" khiến message "added from pack" bị nuốt khi
    // đồng thời có domain rewrite).
    let domainRewritten = false;
    let packVarsAdded = false;
    // Domain rewrite trên mọi string value
    for (const [k, v] of Object.entries(mergedEnv)) {
      if (typeof v === "string" && v.includes("llm.nal.vn")) {
        mergedEnv[k] = v.replace(/llm\.nal\.vn/g, "ai.nal.vn");
        domainRewritten = true;
      }
    }
    if (domainRewritten) {
      changes.push("rewrote legacy llm.nal.vn → ai.nal.vn in env vars");
    }
    // Pack env vars add nếu user chưa có
    if (packTemplate.env) {
      for (const [k, v] of Object.entries(packTemplate.env)) {
        if (!(k in mergedEnv)) {
          mergedEnv[k] = v;
          packVarsAdded = true;
        }
      }
    }
    if (packVarsAdded) {
      changes.push("env vars added from pack");
    }
    if (domainRewritten || packVarsAdded) {
      merged.env = mergedEnv;
    }
  }

  // 5. permissions — union allow + union deny (dedupe).
  if (packTemplate.permissions) {
    const userAllow = userSettings.permissions?.allow || [];
    const userDeny = userSettings.permissions?.deny || [];
    const packAllow = packTemplate.permissions.allow || [];
    const packDeny = packTemplate.permissions.deny || [];
    const mergedAllow = unionDedupe(userAllow, packAllow);
    const mergedDeny = unionDedupe(userDeny, packDeny);
    if (mergedAllow.length !== userAllow.length || mergedDeny.length !== userDeny.length) {
      merged.permissions = { allow: mergedAllow, deny: mergedDeny };
      changes.push(
        `permissions union (+${mergedAllow.length - userAllow.length} allow, +${mergedDeny.length - userDeny.length} deny)`,
      );
    }
  }

  // 6. hooks — merge per-event với dedupe + migrate stale relative-path entries.
  if (packTemplate.hooks) {
    // SECURITY: reject hook command trỏ path RA NGOÀI workspace (absolute / `../`)
    // trước khi merge — chốt chặn supply chain nếu pack template (`.claude/pack/`,
    // populated qua `avatar sync`) bị compromise inject hook trỏ `/malicious/bin`
    // hoặc `../../evil`. Đối xứng với check statusLine (mục 1) + enableTool
    // (apply-tool-manifest-to-settings.ts). Fail-closed: throw abort TOÀN BỘ merge,
    // không skip riêng entry lỗi — pack compromise là supply-chain event, cần
    // dừng cứng + operator awareness.
    for (const [event, entries] of Object.entries(packTemplate.hooks)) {
      for (const entry of entries) {
        const hookList = (entry as { hooks?: Array<{ command?: string }> })?.hooks ?? [];
        for (const h of hookList) {
          if (!h.command) continue;
          const check = isCommandPathWithinWorkspace(workspacePath, h.command);
          if (!check.safe) {
            throw new Error(
              `Pack template hook (${event}) bị từ chối: ${check.reason}. ` +
                `Hook pack PHẢI dùng path tương đối tới workspace (vd \${CLAUDE_PROJECT_DIR}/.claude/...).`,
            );
          }
        }
      }
    }

    const userHooks = userSettings.hooks || {};
    const {
      merged: mergedHooks,
      touchedEvents,
      migratedCount,
    } = mergeHooksPerEvent(packTemplate.hooks, userHooks);
    if (touchedEvents.length > 0 || migratedCount > 0) {
      merged.hooks = mergedHooks;
      if (touchedEvents.length > 0) {
        changes.push(`hooks added for events: ${touchedEvents.join(", ")}`);
      }
      if (migratedCount > 0) {
        changes.push(
          `migrated ${migratedCount} stale relative-path hook entries to use \${CLAUDE_PROJECT_DIR} version (fixes hook failure when shell cwd changes)`,
        );
      }
    }
  }

  // Không có thay đổi gì → skip write.
  if (changes.length === 0) {
    return { action: "no-change", changes: [] };
  }

  // Backup + write.
  let backupPath: string | undefined;
  if (projectHasSettings) {
    backupPath = backupFilename(projectSettingsPath);
    await fs.copyFile(projectSettingsPath, backupPath);
  }
  await writeJsonAtomic(projectSettingsPath, merged);
  return { action: "merged", backupPath, changes };
}
